import React from "react";
import { Clock, Flame, Sun, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { menuData } from "@/data/menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { buildWhatsAppLink } from "@/lib/contact";
import Reveal from "@/components/Reveal";

const AvailabilityTag = ({ type, lang }) => {
  if (type === "all_day") return null;
  const label = { pt: "ATÉ ÀS 18:00h", en: "UNTIL 18:00h", fr: "JUSQU'À 18h", es: "HASTA 18h" }[lang];
  return (
    <div className="mt-3 inline-block px-3 py-1.5 bg-white text-black">
      <span className="text-[9px] font-black tracking-[0.15em] uppercase">{label}</span>
    </div>
  );
};

const ItemRow = ({ item, lang, idx, signatureLabel }) => (
  <div className="py-8 border-b border-white/5 last:border-0 group">
    <div className="flex flex-wrap items-baseline gap-3">
      <span className="text-[10px] font-bold text-[#D4AF37]/40 tracking-[0.2em]">{String(idx + 1).padStart(2, "0")}</span>
      <h4 className="font-serif text-xl text-white font-light group-hover:text-[#D4AF37] transition-colors">
        {item.name[lang] || item.name.en}
      </h4>
      {item.signature && <span className="ml-2 text-[8px] tracking-[0.25em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-0.5 rounded-sm shrink-0">{signatureLabel}</span>}
    </div>
    {item.desc && <p className="mt-2 pl-[34px] text-sm text-white/75 font-light italic">{item.desc[lang] || item.desc.en}</p>}
    <div className="pl-[34px]"><AvailabilityTag type={item.availability} lang={lang} /></div>
  </div>
);

export const MenuSection = () => {
  const { lang, t } = useLang();
  const buildLink = (p) => buildWhatsAppLink(t.whatsappMessage + " (" + p + ")");
  const dayLabel = { en: "DAY", pt: "DIA", fr: "JOUR", es: "DÍA" }[lang];
  const nightLabel = { en: "NIGHT", pt: "NOITE", fr: "SOIR", es: "NOCHE" }[lang];

  return (
    <section id="menu" className="section-pad bg-[var(--ms-night)] relative text-white">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <Reveal className="mb-16">
          <span className="ornament text-[#D4AF37]">{t.menu.overline}</span>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl font-light tracking-tight">{t.menu.title}</h2>
        </Reveal>

        <Reveal delay={150}>
          <Tabs defaultValue="day">
            <TabsList className="bg-white/5 border border-white/10 rounded-full h-auto p-1.5 gap-2 flex w-fit mb-12">
              <TabsTrigger value="day" className="rounded-full px-6 sm:px-10 py-3 text-[10px] font-bold tracking-[0.3em] uppercase data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/75 transition-all">
                <Sun className="h-3.5 w-3.5 mr-2" /> {dayLabel}
              </TabsTrigger>
              <TabsTrigger value="night" className="rounded-full px-6 sm:px-10 py-3 text-[10px] font-bold tracking-[0.3em] uppercase data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/75 transition-all">
                <Flame className="h-3.5 w-3.5 mr-2" /> {nightLabel}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="day" className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              <div className="lg:col-span-8">
                {menuData.day.categories.map((c) => (
                  <div key={c.id}>
                    {c.items.map((it, i) => <ItemRow key={it.id} item={it} lang={lang} idx={i} signatureLabel={t.menu.signatureLabel || "Signature"} />)}
                  </div>
                ))}
              </div>
              <aside className="lg:col-span-4 h-fit bg-white/[0.03] border border-white/10 p-10 rounded-[40px] backdrop-blur-md">
                <div className="flex items-center gap-3 text-[#D4AF37] mb-6"><Clock className="h-5 w-5" /> <span className="text-[10px] font-bold tracking-[0.3em] uppercase">10:00 — 18:00</span></div>
                <p className="font-serif text-2xl font-light mb-8 leading-snug">{t.menu.dayDescription}</p>
                <a href={buildLink(t.menu.tabDay)} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-3 bg-[#D4AF37] text-black py-5 rounded-full font-bold text-[10px] tracking-[0.2em] uppercase hover:bg-white transition-all shadow-xl shadow-[#D4AF37]/10">{t.menu.ctaBook} <ArrowRight className="h-4 w-4" /></a>
              </aside>
            </TabsContent>

            <TabsContent value="night" className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              <div className="lg:col-span-8">
                {menuData.night.categories.map((c) => (
                  <div key={c.id}>
                    {c.items.map((it, i) => <ItemRow key={it.id} item={it} lang={lang} idx={i} signatureLabel={t.menu.signatureLabel || "Signature"} />)}
                  </div>
                ))}
              </div>
              <aside className="lg:col-span-4 h-fit bg-[#D4AF37] p-10 rounded-[40px] text-black shadow-2xl">
                <div className="flex items-center gap-3 mb-6"><Flame className="h-5 w-5" /> <span className="text-[10px] font-black tracking-[0.3em] uppercase">18:00 — 00:00</span></div>
                <p className="font-serif text-2xl font-medium mb-8 leading-snug">{t.menu.nightDescription}</p>
                <a href={buildLink(t.menu.tabNight)} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-3 bg-black text-white py-5 rounded-full font-bold text-[10px] tracking-[0.2em] uppercase shadow-xl transition-all hover:bg-zinc-900">{t.menu.ctaBook} <ArrowRight className="h-4 w-4" /></a>
              </aside>
            </TabsContent>
          </Tabs>
        </Reveal>
      </div>
    </section>
  );
};
